import { FormikProvider, useField, useFormik } from 'formik';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex } from 'reflexbox';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Select, Spinner } from '@treams/treams-ui';

import FilledInBy from './common/FilledInBy';
import Property from './common/Property';

function CompetencyModalSelector() {
	const { t } = useTranslation();

	const { data } = graphql.useCompetenciesWithLevelsQuery({
		variables: {
			isArchived: false,
			first: 1000,
		},
	});

	const { localize } = useLocalize();

	const [field, , { setValue }] = useField('competency');

	const options =
		data?.competencies.edges
			.slice()
			.sort((a, b) => localize(a.node.title).text.localeCompare(localize(b.node.title).text))
			.map((edge) => ({
				label: localize(edge.node.title).text,
				value: edge.node.id,
			})) ?? [];

	return (
		<Property title={t('common.settings.evaluationForms.properties.competency.title')}>
			{data ? (
				<Select
					selectedValue={field.value?.id}
					onChange={(value) => {
						if (!value) {
							return;
						}

						setValue(data.competencies.edges.find((edge) => edge.node.id === value)?.node);
					}}
					options={options.map((option) => ({
						title: option.label,
						value: option.value,
					}))}
				/>
			) : (
				<Spinner centered />
			)}
		</Property>
	);
}

export default function CompetencyModal(props: {
	item: graphql.EvaluationCompetencyItem;
	fillInBy: graphql.EvaluationFillInBy;
	onChange: (item: graphql.EvaluationCompetencyItem, fillInBy: graphql.EvaluationFillInBy) => void;
}) {
	const formik = useFormik({
		initialValues: {
			competency: props.item.competency,
			weight: props.item.weight,
			fillInBy: props.fillInBy,
		},
		onSubmit: () => {},
	});

	useEffect(() => {
		if (formik.values.competency?.id) {
			props.onChange(
				{
					...props.item,
					competency: formik.values.competency,
					weight: formik.values.weight,
				},
				formik.values.fillInBy,
			);
		}
	}, [formik.values.competency?.id, formik.values.weight, formik.values.fillInBy]);

	return (
		<FormikProvider value={formik}>
			<Flex p={3} flexDirection="column">
				<CompetencyModalSelector />
				<FilledInBy />
			</Flex>
		</FormikProvider>
	);
}
